// window.location.hostname is [LegacyUnforgeable] and cannot be overridden via
// Object.defineProperty in a real browser. This wrapper lets Playwright inject a
// fake hostname via page.addInitScript before app code runs:
//   window.__resolveHostname = () => 'folio.tataamc.com'
// The seam is ignored in production so third-party scripts can't hijack resolution.
export const resolveHostname = (): string => {
  const mode = (import.meta as unknown as { env?: { MODE?: string } }).env?.MODE;
  if (mode !== 'production') {
    const seam = (window as Window & { __resolveHostname?: () => string }).__resolveHostname;
    if (seam) return seam();
  }
  return window.location.hostname;
};

export const isGlomoPayDomain = (hostname: string): boolean => /\.glomopay\.com$/.test(hostname) || hostname === 'localhost';

type TResolveTenantDomainOptions = {
  // Query param that overrides the host on localhost (local dev / e2e).
  queryParam?: string;
};

// The host to resolve tenant branding for. Always resolves (no skip) — used by
// apps that brand every host, like the investor portal.
export const resolveTenantDomain = ({ queryParam = 'tenant_domain' }: TResolveTenantDomainOptions = {}): string => {
  const hostname = resolveHostname();
  const override = new URLSearchParams(window.location.search).get(queryParam);
  return override && hostname === 'localhost' ? override : hostname;
};

type TDomainResolution = {
  domain: string;
  skipApi: boolean;
};

type TGetDomainForResolutionOptions = TResolveTenantDomainOptions & {
  // Hosts whose resolution is skipped in favour of default branding
  // (merchant-dashboard skips its own *.glomopay.com hosts).
  isDefaultDomain?: (hostname: string) => boolean;
};

// Host + a skip decision, for apps that skip resolution on their own domains
// (merchant-dashboard). Apps that always resolve should use resolveTenantDomain.
export const getDomainForResolution = (options: TGetDomainForResolutionOptions = {}): TDomainResolution => {
  const { queryParam = 'tenant_domain', isDefaultDomain = isGlomoPayDomain } = options;

  const hostname = resolveHostname();
  const override = new URLSearchParams(window.location.search).get(queryParam);

  if (override && hostname === 'localhost') return { domain: override, skipApi: false };

  if (isDefaultDomain(hostname)) return { domain: hostname, skipApi: true };

  return { domain: hostname, skipApi: false };
};
